import React from 'react';
import styled from 'styled-components';

// Button position variants for consistent spacing
export type ButtonPosition = 
  | 'home'      // 50% - 30px (above center)
  | 'share'     // 50% + 30px (below center)  
  | 'speed'     // 50% + 90px (further below)
  | 'pwa'       // 50% + 150px (bottom area)
  | 'notification'; // 50% + 210px (very bottom)

// Props interface
interface MobileNavigationButtonProps {
  position: ButtonPosition;
  onClick?: (e: React.MouseEvent) => void;
  onTouchEnd?: (e: React.TouchEvent) => void;
  disabled?: boolean;
  title?: string;
  'aria-label'?: string;
  children: React.ReactNode;
  className?: string;
  href?: string; // For anchor tags
  target?: string;
  rel?: string;
}

// Position mapping for consistent spacing
const getTopPosition = (position: ButtonPosition): string => {
  switch (position) {
    case 'home': return 'calc(50% - 30px)';
    case 'share': return 'calc(50% + 30px)';
    case 'speed': return 'calc(50% + 90px)';
    case 'pwa': return 'calc(50% + 150px)';
    case 'notification': return 'calc(50% + 210px)';
    default: return 'calc(50% - 30px)';
  }
};

// Shared styled button base with consistent alignment
const BaseNavigationButton = styled.button<{ $position: ButtonPosition }>`
  /* ✅ CONSISTENT POSITIONING - All buttons aligned */
  position: absolute;
  right: 16px; /* Standard right margin for all mobile buttons */
  top: ${({ $position }) => getTopPosition($position)};
  
  /* ✅ STANDARDIZED APPEARANCE */
  background: none;
  border: none;
  width: 48px;
  height: 48px;
  
  /* ✅ CONSISTENT LAYOUT */
  display: flex;
  justify-content: center;
  align-items: center;
  
  /* ✅ STANDARD STYLING */
  color: white;
  cursor: pointer;
  z-index: 3;
  padding: 6px;
  text-decoration: none;
  
  /* ✅ CONSISTENT INTERACTIONS */
  &:hover {
    opacity: 0.8;
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  /* ✅ BOX MODEL STANDARDIZATION */
  box-sizing: border-box;
  outline: none;
  
  /* ✅ ACCESSIBILITY */
  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.8);
    outline-offset: 2px;
  }
`;

// Styled anchor version for links
const BaseNavigationLink = styled.a<{ $position: ButtonPosition }>`
  /* ✅ IDENTICAL STYLING TO BUTTON */
  position: absolute;
  right: 16px;
  top: ${({ $position }) => getTopPosition($position)};
  
  background: none;
  border: none;
  width: 48px;
  height: 48px;
  
  display: flex;
  justify-content: center;
  align-items: center;
  
  color: white;
  cursor: pointer;
  z-index: 3;
  padding: 6px;
  text-decoration: none;
  
  &:hover {
    opacity: 0.8;
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  box-sizing: border-box;
  outline: none;
  
  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.8);
    outline-offset: 2px;
  }
`;

/**
 * Shared Mobile Navigation Button Component
 * 
 * Ensures consistent positioning and styling across all mobile navigation buttons.
 * Prevents alignment issues by standardizing the layout approach.
 * 
 * @param position - Button position variant (home, share, speed, pwa, notification)
 * @param onClick - Click handler for buttons
 * @param onTouchEnd - Touch handler for mobile interactions
 * @param disabled - Whether button is disabled
 * @param href - URL for link variants (creates <a> tag instead of <button>)
 * @param children - Button content (icon, text, etc.)
 */
const MobileNavigationButton: React.FC<MobileNavigationButtonProps> = ({
  position,
  onClick,
  onTouchEnd,
  disabled = false,
  title,
  'aria-label': ariaLabel,
  children,
  className,
  href,
  target,
  rel,
  ...props
}) => {
  // Use anchor tag for links, button for interactions
  if (href) {
    return (
      <BaseNavigationLink
        $position={position}
        href={href}
        target={target}
        rel={rel}
        title={title}
        aria-label={ariaLabel}
        className={className}
        onClick={onClick}
        onTouchEnd={onTouchEnd}
        {...props}
      >
        {children}
      </BaseNavigationLink>
    );
  }

  return (
    <BaseNavigationButton
      $position={position}
      onClick={onClick}
      onTouchEnd={onTouchEnd}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel}
      className={className}
      {...props}
    >
      {children}
    </BaseNavigationButton>
  );
};

export default MobileNavigationButton; 